
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useQuotes } from '@/hooks/use-quotes';
import { PEOQuote } from '@/types/quotes.types';
import { Loader2, FileText, Check, X, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';

const QuotePage = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: quotes, isLoading: quotesLoading, error } = useQuotes();
  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in to view your quotes",
        variant: "destructive",
      });
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate, toast]);

  if (authLoading || quotesLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-4xl py-12">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold text-destructive">Error Loading Quotes</h2>
              <p>{error.message || "There was an error loading your quotes. Please try again later."}</p>
              <Button onClick={() => navigate('/')} variant="outline">Return to Dashboard</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quotes || quotes.length === 0) {
    return (
      <div className="container max-w-4xl py-12">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4 py-8">
              <h2 className="text-2xl font-bold">No Quotes Available</h2>
              <p className="text-muted-foreground">
                No quotes have been prepared for your application yet. Our team is working on getting 
                the best options for you.
              </p>
              <p className="text-sm text-muted-foreground">
                You'll receive an email notification when quotes are ready for your review.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">Return to Dashboard</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getQuotesByStatus = (status: string) => {
    if (status === "all") return quotes;
    return quotes.filter(quote => quote.status === status);
  };

  const displayQuotes = getQuotesByStatus(activeTab);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-slate-100">Pending Review</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Accepted</Badge>;
      case 'declined':
        return <Badge variant="outline" className="bg-red-100 text-red-800">Declined</Badge>;
      case 'interested':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">Interested</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="container max-w-4xl py-12">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Your PEO Quotes</h1>
          <p className="text-muted-foreground">Review personalized quotes for your business</p>
        </div>

        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all">All ({quotes.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({getQuotesByStatus('pending').length})</TabsTrigger>
            <TabsTrigger value="interested">Interested ({getQuotesByStatus('interested').length})</TabsTrigger>
            <TabsTrigger value="accepted">Accepted ({getQuotesByStatus('accepted').length})</TabsTrigger>
          </TabsList>
          <TabsContent value={activeTab} className="mt-6">
            <div className="space-y-4">
              {displayQuotes.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No quotes in this category</p>
                  </CardContent>
                </Card>
              ) : (
                displayQuotes.map((quote: PEOQuote) => (
                  <Card key={quote.id} className="overflow-hidden">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center justify-between">
                        <span>{quote.provider_name || "PEO Provider"}</span>
                        {getStatusBadge(quote.status)}
                      </CardTitle>
                      <CardDescription>
                        Quote created {quote.created_at ? formatDistanceToNow(new Date(quote.created_at), { addSuffix: true }) : 'recently'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-sm space-y-1">
                        <div className="font-medium">Quote Summary:</div>
                        <p>{quote.quote_summary || "No summary provided"}</p>
                      </div>
                      
                      {(quote.monthly_cost || quote.annual_cost) && (
                        <div className="grid grid-cols-2 gap-4 p-4 rounded-md bg-slate-50">
                          {quote.monthly_cost && (
                            <div>
                              <p className="text-sm text-muted-foreground">Monthly Cost</p>
                              <p className="text-xl font-bold">${quote.monthly_cost.toLocaleString()}</p>
                            </div>
                          )}
                          {quote.annual_cost && (
                            <div>
                              <p className="text-sm text-muted-foreground">Annual Cost</p>
                              <p className="text-xl font-bold">${quote.annual_cost.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-2">
                        {quote.quote_file_url && (
                          <Button variant="outline" className="gap-2" onClick={() => window.open(quote.quote_file_url, '_blank')}>
                            <FileText className="h-4 w-4" />
                            View Quote Details
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default QuotePage;
